import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FOUNDATION_REPLACEMENT_GUID } from '@scene'
import { AxiosError } from 'axios'
import DeleteIcon from '@mui/icons-material/Delete'
import Grid3x3Icon from '@mui/icons-material/Grid3x3'
import { Alert, Button, IconButton, Stack, Typography } from '@mui/material'
import { ThreeEvent } from '@react-three/fiber'
import { useMutation } from '@tanstack/react-query'
import { Section } from '@ui2/structure'
import { SwitchLabeled } from '@ui/actions'
import { Form, FormControlledSwitchLabeled, Select, UnitConvertedField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { CollapsibleBoxInternalState } from '@ui/structure'
import { useControlStore, useElementSelectionStore, useModelStore } from '@editorStores'
import {
  useElementLabel,
  useModelClickListeners,
  useSelectionMode,
  useTypeInteraction,
} from '@editorHooks'
import { downLoadFile } from '@utils'
import { getExportDocument } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import FoundationLoadsDownloadButton from './components/FoundationLoadsDownloadButton'
import { MicroFeTemplate, microfeDownloadFilename } from './microfeDownloadFilename'

export const FOUNDATION_LOADS_TARGET_SELECTION_KEY = 'foundation-loads-target-element'

const selectableElementTypes: ElementTypes[] = ['foundation', 'vertical_slabs']

interface FoundationLoadsSettings {
  smoothLoads: boolean
  pointLoadThreshold: number
  microfeTemplate: MicroFeTemplate
  microfeIncludeWind: boolean
  microfeIncludeEarthquake: boolean
}

interface FoundationLoadsDownload {
  documentPath: string
  type: BlobType
  extension: string
  label: string
  dataCy: string
  params?: (settings: FoundationLoadsSettings) => Record<string, string>
  filename?: (projectName: string, settings: FoundationLoadsSettings) => string
}

const defaultSettings: FoundationLoadsSettings = {
  smoothLoads: false,
  pointLoadThreshold: 0,
  microfeTemplate: 'bodenplatte',
  microfeIncludeWind: true,
  microfeIncludeEarthquake: true,
}

const microfeTemplateOptions: { value: MicroFeTemplate; label: string }[] = [
  { value: 'bodenplatte', label: 'Bodenplatte' },
  { value: 'wu-bodenplatte', label: 'Bodenplatte mit WU-Anforderungen' },
  { value: 'kellerdecke', label: 'Kellerdecke' },
]

const envelopeTooltip = (loads: string): TooltipContents => ({
  text: `${loads} werden als Umhüllende in einen eigenen Lastfall exportiert, je Position mit der größten abwärts gerichteten Last. Abhebende Lasten werden nicht exportiert.`,
})

const tableDownloads: FoundationLoadsDownload[] = [
  {
    documentPath: 'foundation-loads/table',
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
    label: 'Fundamentlasten (XLSX)',
    dataCy: 'btn-download-foundation-loads-xlsx',
  },
  {
    documentPath: 'foundation-loads/rfem',
    type: 'application/json',
    extension: 'json',
    label: 'Fundamentlasten (RFEM)',
    dataCy: 'btn-download-foundation-loads-rfem',
  },
]

const microfeDownload: FoundationLoadsDownload = {
  documentPath: 'foundation-loads/microfe',
  type: 'application/zip',
  extension: 'mbpma',
  label: 'Fundamentlasten (MicroFe)',
  dataCy: 'btn-download-foundation-loads-microfe',
  params: settings => ({
    template: settings.microfeTemplate,
    include_wind: settings.microfeIncludeWind.toString(),
    include_earthquake: settings.microfeIncludeEarthquake.toString(),
  }),
  filename: (projectName, settings) =>
    microfeDownloadFilename({
      projectName,
      template: settings.microfeTemplate,
      includeWind: settings.microfeIncludeWind,
      includeEarthquake: settings.microfeIncludeEarthquake,
    }),
}

const FoundationLoadsExport = (): ReactElement => {
  const { projectId } = useParams()
  const project = useModelStore(state => state.project)
  const { enqueueSnackbar } = useSnackbar()
  const getLabel = useElementLabel()

  const [specifyElement, setSpecifyElement] = useState(false)
  const [selectedElementGuid, setSelectedElementGuid] = useState<string | null>(null)
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null)

  const {
    setSelectionMode,
    unsetSelectionMode,
    selectionModeKey: activeSelectionModeKey,
  } = useSelectionMode()
  const setHighlightedIds = useElementSelectionStore(state => state.setHighlightedIds)
  const resetHighlightedIds = useElementSelectionStore(state => state.resetHighlightedIds)
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)

  const previousProblemViewRef = useRef({ errors: true, warnings: true })
  const isSelectingElement = activeSelectionModeKey === FOUNDATION_LOADS_TARGET_SELECTION_KEY

  useTypeInteraction(selectableElementTypes, specifyElement && isSelectingElement)

  const selectElement = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { elementType } = event.object.userData as { elementType?: string }
    if (!selectableElementTypes.includes(elementType as ElementTypes)) return

    setSelectedElementGuid(event.object.name)
  }, [])

  useModelClickListeners(selectElement, [selectElement], specifyElement && isSelectingElement, true)

  useEffect(() => {
    if (!specifyElement || !selectedElementGuid) return
    setHighlightedIds([selectedElementGuid])
    return () => resetHighlightedIds()
  }, [selectedElementGuid, setHighlightedIds, resetHighlightedIds, specifyElement])

  useEffect(() => {
    if (!specifyElement) {
      setSelectedElementGuid(null)
      if (isSelectingElement) unsetSelectionMode()
    }
  }, [specifyElement, isSelectingElement, unsetSelectionMode])

  const onStartSelectingElement = useCallback(() => {
    previousProblemViewRef.current = {
      errors: problemViewActiveErrors,
      warnings: problemViewActiveWarnings,
    }
    setProblemViewActiveErrors(false)
    setProblemViewActiveWarnings(false)
    setSelectionMode({
      key: FOUNDATION_LOADS_TARGET_SELECTION_KEY,
      message:
        'Klicken Sie im Modell auf ein Fundamentelement für den Fundamentlasten-Export. Schließen Sie diese Meldung zum Beenden.',
      onAbort: () => {
        setProblemViewActiveErrors(previousProblemViewRef.current.errors)
        setProblemViewActiveWarnings(previousProblemViewRef.current.warnings)
      },
    })
  }, [
    problemViewActiveErrors,
    problemViewActiveWarnings,
    setProblemViewActiveErrors,
    setProblemViewActiveWarnings,
    setSelectionMode,
  ])

  const targetElementId = specifyElement ? selectedElementGuid : FOUNDATION_REPLACEMENT_GUID
  const projectLabel = project.name || projectId || ''
  const foundationLoadsDownloadFilename = `Fundamentlasten${
    selectedElementGuid ? ' - ' + selectedElementGuid : ''
  } - ${projectLabel}`

  const buildQueryParams = useCallback(
    (formSettings: FoundationLoadsSettings) => ({
      point_load_threshold: formSettings.pointLoadThreshold.toString(),
      apply_smoothing: formSettings.smoothLoads.toString(),
      targetElementId: targetElementId ?? FOUNDATION_REPLACEMENT_GUID,
    }),
    [targetElementId],
  )

  const { mutate: downloadExportDocument, isLoading: isDownloading } = useMutation(
    async ({
      documentPath,
      type,
      filename,
      extension,
      params,
    }: {
      documentPath: string
      type: BlobType
      filename: string
      extension: string
      params: Record<string, string>
    }) => {
      setDownloadingPath(documentPath)
      const data = await getExportDocument.request(projectId as string, documentPath, params)
      return {
        data,
        type,
        filename: `${filename}.${extension}`,
      }
    },
    {
      onSuccess: async ({ data, type, filename }) => {
        if (!data) {
          enqueueSnackbar('Ergebnisse noch nicht vorhanden', { variant: 'warning' })
          return
        }
        downLoadFile(data, type, filename)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Abrufen des Dokuments'), {
          variant: 'error',
        })
      },
      onSettled: () => setDownloadingPath(null),
    },
  )

  const isDownloadDisabled = specifyElement && !selectedElementGuid

  const startDownload = useCallback(
    (download: FoundationLoadsDownload, settings: FoundationLoadsSettings) =>
      downloadExportDocument({
        documentPath: download.documentPath,
        type: download.type,
        filename: download.filename?.(projectLabel, settings) ?? foundationLoadsDownloadFilename,
        extension: download.extension,
        params: { ...buildQueryParams(settings), ...download.params?.(settings) },
      }),
    [downloadExportDocument, foundationLoadsDownloadFilename, projectLabel, buildQueryParams],
  )

  return (
    <CollapsibleBoxInternalState
      variant="expander"
      label="Fundamentlasten"
      dataCy="foundation-loads-export"
      defaultExpanded
      startIcon={<Grid3x3Icon sx={{ color: 'grey.600', padding: '2px' }} />}
    >
      <Form defaultValues={defaultSettings} onSubmit={() => undefined}>
        {methods => (
          <Stack direction="column" spacing={2} width="100%">
            <Section title="Lastaufbereitung">
              <Stack direction="column" spacing={1} pb={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Gilt für alle Downloads.
                </Typography>
                <SwitchLabeled
                  checked={methods.watch('smoothLoads')}
                  onChange={event => methods.setValue('smoothLoads', event.target.checked)}
                  label="Lasten vor Export glätten?"
                  infoTooltip={{
                    title:
                      'Wandlasten werden je Lastfall über die gesamte Wandlänge zu einer gleichmäßigen Linienlast geglättet. Punktlasten bis zum Grenzwert fließen in die Glättung ein, größere Punktlasten bleiben als Punktlasten erhalten. Der Grenzwert gilt pro Lastfall für die charakteristische Lastgröße.',
                  }}
                  data-cy="foundation-loads-smooth-switch"
                />

                <UnitConvertedField
                  name="pointLoadThreshold"
                  conversionFactor={1000}
                  inputLabel="Grenzwert für Punktlasten"
                  unitLabel="kN"
                />

                <SwitchLabeled
                  checked={specifyElement}
                  onChange={event => setSpecifyElement(event.target.checked)}
                  label="Element angeben?"
                  infoTooltip={{
                    title:
                      'Wählen Sie das Fundamentelement im Modell, für das die Lasten exportiert werden sollen.',
                  }}
                  data-cy="foundation-loads-specify-element-switch"
                />

                {specifyElement && (
                  <Stack direction="column" spacing={1} width="100%">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography align="center" noWrap sx={{ width: '100%' }}>
                        {selectedElementGuid ? '1 Element ausgewählt' : 'Kein Element ausgewählt'}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onStartSelectingElement}
                        fullWidth
                        disabled={isDownloading}
                        data-cy="btn-select-foundation-loads-target"
                      >
                        Element auswählen
                      </Button>
                    </Stack>
                    {selectedElementGuid && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography noWrap align="center" sx={{ width: '100%' }}>
                          {getLabel(selectedElementGuid)}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="Element entfernen"
                          onClick={() => setSelectedElementGuid(null)}
                          sx={{ padding: 0 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Section>

            <Section title="Excel & RFEM">
              <Stack direction="column" spacing={1} pb={1.5}>
                {tableDownloads.map(download => (
                  <FoundationLoadsDownloadButton
                    key={download.documentPath}
                    label={download.label}
                    loading={isDownloading && downloadingPath === download.documentPath}
                    disabled={isDownloadDisabled || isDownloading}
                    onClick={() =>
                      startDownload(download, methods.getValues() as FoundationLoadsSettings)
                    }
                    data-cy={download.dataCy}
                  />
                ))}
              </Stack>
            </Section>

            <Section title="MicroFe (mb WorkSuite)" hideBorderBottom>
              <Stack direction="column" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Diese Einstellungen gelten nur für den MicroFe-Export.
                </Typography>
                <Select<FoundationLoadsSettings>
                  name="microfeTemplate"
                  label="MicroFe-Vorlage"
                  options={microfeTemplateOptions}
                  data-cy="foundation-loads-microfe-template"
                />
                <FormControlledSwitchLabeled
                  name="microfeIncludeWind"
                  label="Windlasten (Umhüllende)"
                  tooltip={envelopeTooltip('Windlasten aller Richtungen')}
                  data-cy="foundation-loads-microfe-wind-switch"
                />
                <FormControlledSwitchLabeled
                  name="microfeIncludeEarthquake"
                  label="Erdbebenlasten (Umhüllende)"
                  tooltip={envelopeTooltip('Erdbebenlasten aller Lastfälle')}
                  data-cy="foundation-loads-microfe-earthquake-switch"
                />
                <Alert severity="info" data-cy="foundation-loads-microfe-live-load-hint">
                  Nutzlasten der Kategorien A1, A2, A3, B2 und F1 werden als Qk.N-Lastfälle mit der
                  Bezeichnung aus dem mb-Lastkatalog exportiert. Andere Nutzlastkategorien werden
                  noch nicht zugeordnet: Ihre Lastfälle sind mit „Einwirkung prüfen“ markiert und
                  müssen in MicroFe kontrolliert werden.
                </Alert>
                <FoundationLoadsDownloadButton
                  label={microfeDownload.label}
                  loading={isDownloading && downloadingPath === microfeDownload.documentPath}
                  disabled={isDownloadDisabled || isDownloading}
                  onClick={() =>
                    startDownload(microfeDownload, methods.getValues() as FoundationLoadsSettings)
                  }
                  data-cy={microfeDownload.dataCy}
                />
              </Stack>
            </Section>
          </Stack>
        )}
      </Form>
    </CollapsibleBoxInternalState>
  )
}

export default FoundationLoadsExport
